import React, { useCallback, useRef, useState, useEffect } from "react";
import type { NotificationType } from "../../../../../../common-deprecated/types/CommonTypes";
import { useDebouncedEffect } from "../../../../../../common-deprecated/hooks";
import Text from "../../../styles/atoms/Text";
import Button from "../../../styles/atoms/Button";
import Icon from "../../../styles/atoms/Icon";
import * as Styles from "./Notification.styles";
import { SrOnly } from "../../../styles/utils/Accessibility.styles";
import { isRetailer } from "../../../../../../common-deprecated/utils";
import { useCommonSelector } from "../../../../../../common-deprecated/redux/commonStore";

type NotificationPropsType = {
    notification: NotificationType;
    index: number;
    onTransitionEnd: (index: number) => void;
};

const Notification = (props: NotificationPropsType): JSX.Element => {
    const { notification, index, onTransitionEnd } = props;
    const commonSettings = useCommonSelector((state) => state.commonSettings);
    const ref = useRef<HTMLDivElement | null>(null);
    const interval = useRef<NodeJS.Timer>();
    const [open, setOpen] = useState<boolean>(true);
    const [timer, setTimer] = useState<number>(0);
    const [userIsFocused, setUserIsFocused] = useState(false);
    const totalTimeVisible = 8000; // 8seconds is the standard amount for a timer when it comes to accesible notifications

    const closeNotification = useCallback(() => {
        setOpen(false);
    }, []);

    useDebouncedEffect(
        () => {
            if (open && timer >= totalTimeVisible && interval.current) {
                clearInterval(interval.current);
                closeNotification();
            }
        },
        500,
        [timer, interval, open],
    );

    useEffect(() => {
        if (open) {
            interval.current = setInterval(() => {
                if (!userIsFocused) {
                    setTimer((prevTimer) => prevTimer + 1000);
                }
            }, 1000);
        }

        return () => clearInterval(interval.current);
    }, [userIsFocused, open]);

    return (
        <Styles.Wrapper
            // Every notification needs to have a seperate margin value.
            key={notification.displaySettings?.notificationIndex ?? index}
            ref={ref}
            isSlideOut={!open}
            isSlideIn={open}
            onAnimationEnd={() => {
                if (!open) {
                    onTransitionEnd(notification.displaySettings?.notificationIndex ?? index);
                }
            }}
            onMouseOver={() => setUserIsFocused(true)}
            onMouseOut={() => setUserIsFocused(false)}
            open={open}
            aria-live="polite"
            isRetailer={isRetailer(commonSettings)}
        >
            {notification.title && <SrOnly as="h2">{notification.title}</SrOnly>}
            <Styles.Content>
                {notification.description && (
                    <Text variant="body-small-regular" color="primary-white">
                        {notification.description}
                    </Text>
                )}
                <Styles.Actions>
                    {/* TODO @dylancl : Implement show basket button */}
                    {/* <Button
                        variant="pack-negative"
                        size="small"
                        onClick={() => {
                            closeNotification();
                        }}
                    >
                        <Icon variant="basket-variant" />
                        <UscLabel label="uscNotificationBasketShow" />
                    </Button> */}
                    <Button variant="control-bordered" size="small" onClick={closeNotification}>
                        <Icon variant="close" />
                    </Button>
                </Styles.Actions>
            </Styles.Content>
        </Styles.Wrapper>
    );
};

export default Notification;
